/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';
    this.pauseOnHover = this.element.dataset.pauseOnHover === 'true';
    this.isHovered = false;

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Bind pause-on-hover listeners on the Swiper element
   */
  bindPauseOnHover() {
    if (!this.pauseOnHover || !this.swiperEl || this.pauseHoverBound) return;

    const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    this.pauseHoverBound = true;

    this._onMouseEnter = () => {
      if (!this.swiper) return;

      this.isHovered = true;

      try {
        this.swiper.autoplay.stop();
      } catch (error) {
        // Swiper autoplay can throw when partially initialized
      }

      this.swiper.animating = false;

      if (this._resumeEndHandler) {
        wrapper.removeEventListener('transitionend', this._resumeEndHandler);
        this._resumeEndHandler = null;
      }

      const tx = new DOMMatrix(window.getComputedStyle(wrapper).transform).m41;
      this._frozenTx = tx;

      wrapper.style.setProperty('transition-duration', '0ms', 'important');
      wrapper.style.setProperty('transform', `translate3d(${tx}px, 0px, 0px)`, 'important');
    };

    this._onMouseLeave = () => {
      if (!this.swiper) return;

      this.isHovered = false;

      wrapper.style.removeProperty('transition-duration');
      wrapper.style.removeProperty('transform');

      const idx = this.swiper.activeIndex;
      const targetTx = -this.swiper.snapGrid[idx];
      const remainDist = Math.abs(targetTx - this._frozenTx);
      const prevIdx = idx > 0 ? idx - 1 : 0;
      const fullDist = Math.abs(this.swiper.snapGrid[idx] - this.swiper.snapGrid[prevIdx]) || 1;
      const resumeSpeed = Math.max(1, Math.round((remainDist / fullDist) * this.swiper.params.speed));

      this.swiper.setTranslate(this._frozenTx);
      this.swiper.animating = false;
      this.swiper.slideTo(idx, resumeSpeed);

      this._resumeEndHandler = () => {
        this._resumeEndHandler = null;
        if (this.swiper) {
          this.swiper.animating = false;
          try {
            this.swiper.autoplay.start();
          } catch (error) {
            // Swiper autoplay can throw when partially initialized
          }
        }
      };

      wrapper.addEventListener('transitionend', this._resumeEndHandler, { once: true });
    };

    this.swiperEl.addEventListener('mouseenter', this._onMouseEnter);
    this.swiperEl.addEventListener('mouseleave', this._onMouseLeave);
  }

  /**
   * Remove pause-on-hover listeners
   */
  unbindPauseOnHover() {
    if (!this.swiperEl || !this.pauseHoverBound) return;

    if (this._onMouseEnter) {
      this.swiperEl.removeEventListener('mouseenter', this._onMouseEnter);
      this._onMouseEnter = null;
    }

    if (this._onMouseLeave) {
      this.swiperEl.removeEventListener('mouseleave', this._onMouseLeave);
      this._onMouseLeave = null;
    }

    if (this._resumeEndHandler) {
      const wrapper = this.swiperEl.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.removeEventListener('transitionend', this._resumeEndHandler);
      }
      this._resumeEndHandler = null;
    }

    this.pauseHoverBound = false;
    this.isHovered = false;
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.marquee-content');
    if (!this.contentEl) return;

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => !excludedTags.includes(el.tagName));
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);

    // Ultra-wide viewports: if the slide strip is barely wider than the container, Swiper hits
    // `maxTranslate`, reports `isEnd`, and `slideNext` / autoplay stop (loop cannot wrap). Duplicate
    // the seed slides until the track is at least ~2× the container width.
    const containerWidth = this.element.clientWidth || 0;
    const minTrackWidth = containerWidth > 0 ? containerWidth * 2 : 0;
    if (minTrackWidth > 0 && wrapperEl.scrollWidth < minTrackWidth) {
      const seedSlides = [...wrapperEl.children];
      let guard = 0;
      while (wrapperEl.scrollWidth < minTrackWidth && guard < 30) {
        seedSlides.forEach((slide) => {
          wrapperEl.appendChild(slide.cloneNode(true));
        });
        guard += 1;
      }
    }

    // Get options from data attributes
    const spaceBetween = parseInt(this.element.dataset.spaceBetween) || 120;
    const speed = parseInt(this.element.dataset.speed) || 3000;

    const slideCount = wrapperEl.children.length;

    // Build marquee options using block variables
    const marqueeOptions = {
      slidesPerView: 'auto',
      spaceBetween: spaceBetween,
      centeredSlides: true,
      loop: true,
      loopedSlides: Math.max(slideCount, 10),
      speed: speed,
      allowTouchMove: false,
      autoplay: {
        delay: 1,
        disableOnInteraction: false,
      },
      // Autoplay pauses until `transitionend` on the wrapper; with loop + auto-width slides that
      // event can be missed, leaving the marquee frozen (especially on very wide viewports).
      on: {
        slideChangeTransitionEnd: (swiper) => {
          if (this.isHovered) return;
          if (swiper.destroyed || !swiper.autoplay?.running || !swiper.autoplay.paused) return;
          swiper.autoplay.resume();
        },
        init: (swiper) => {
          const kickIfStuck = () => {
            if (this.isHovered) return;
            if (swiper.destroyed || !swiper.autoplay?.running) return;
            if (swiper.animating) return;
            if (swiper.autoplay.paused) {
              swiper.autoplay.resume();
            }
          };
          requestAnimationFrame(kickIfStuck);
          setTimeout(kickIfStuck, 250);
        },
      },
    };
    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, marqueeOptions);

    this.element.setAttribute('data-swiper-initialized', 'true');

    if (this.pauseOnHover && window.innerWidth >= MOBILE_BREAKPOINT) {
      this.bindPauseOnHover();
    }

    this.isInitialized = true;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    this.unbindPauseOnHover();

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.element.removeAttribute('data-swiper-initialized');
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    this.unbindPauseOnHover();
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-marquee-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Export marquee-specific globals (do not clobber slider.js exports)
window.MarqueeBlock = SliderBlock;
window.initMarqueeBlocks = initSliderBlocks;

